import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useDashboardStore } from "@/state/dashboardStore";

/**
 * 옛 상세 링크(`?ns=`)를 새 경로로 옮깁니다. (#111)
 * --------------------------------------------------------------------------
 * Workload·Pod 상세는 `/namespaces/{ns}/...`로 옮겨졌습니다. 이미 나가 있는
 * 북마크·공유 링크·저장된 최근 항목은 그대로 두면 catch-all에 걸려 홈으로
 * 튕깁니다. 여기서 한 번 `replace`로 바꿔 주면 옛 링크가 계속 같은 리소스를 엽니다.
 *
 * namespace를 정할 수 없을 때는 조용히 홈으로 보내지 않습니다 — 왜 못 여는지와
 * 다음에 할 일을 보여줍니다. "데이터 없음"과 "특정 불가"는 다른 상태입니다.
 */
export function LegacyDetailRedirect({ kind }: { kind: "workload" | "pod" }) {
  const { search } = useLocation();
  const params = useParams();
  const global = useDashboardStore((s) => s.namespace);

  const query = new URLSearchParams(search);
  const fromQuery = query.get("ns") ?? "";
  query.delete("ns");
  /* 링크가 실어 온 ns가 먼저입니다. 없으면 전역 선택으로 보정하되, `all`은
     namespace가 아니라 "고르지 않았다"는 UI 값이라 신원이 되지 못합니다. */
  const namespace = fromQuery || (global !== "all" ? global : "");

  if (!namespace) {
    return (
      <div className="page">
        <div className="state" role="alert">
          <span className="state__glyph" aria-hidden="true">
            ?
          </span>
          <span className="state__title">어느 Namespace의 리소스인지 알 수 없습니다</span>
          <span className="state__detail">
            이 링크는 Namespace 정보가 없는 옛 형식입니다. 목록에서 다시 찾아 주세요.
          </span>
          <Link to="/namespaces">Namespace 목록으로</Link>
        </div>
      </div>
    );
  }

  const ns = encodeURIComponent(namespace);
  const rest = query.toString();
  const tail =
    kind === "pod"
      ? `pods/${encodeURIComponent(params.name ?? "")}`
      : `workloads/${params.kind ?? "Deployment"}/${encodeURIComponent(params.name ?? "")}`;
  return <Navigate to={`/namespaces/${ns}/${tail}${rest ? `?${rest}` : ""}`} replace />;
}
